import {
  Body,
  Controller,
  Input,
  Stack,
  TextInput,
  useForm,
} from '@qred/components-library';
import { Select } from '@qred/components-library/next';
import React, { useEffect, useState } from 'react';
import { FieldPath } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { AddressSearch } from '~/components/shared/AddressSearch/AddressSearch';
import { DocumentUploadSection } from '~/components/shared/DocumentUpload/DocumentUploadSection';
import { RadioGroup } from '~/components/shared/RadioGroup/RadioGroup';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import StepHeader from '~/components/shared/Stepper/components/StepHeader';
import { DocumentType, fileConfigByDocumentType } from '~/constants/fileUpload';
import {
  getPersonalNumberPlaceholder,
  marketHasTrustpilot,
} from '~/constants/markets';
import { formatPersonalNumber } from '~/helpers/formatters.helper';
import { RootState } from '~/store';

import TrustpilotWidget from '~/components/shared/TrustpilotWidget/TrustpilotWidget';
import { ClientAssessmentQuestionnaireTypeId } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useDocumentUpload } from '~/hooks/useDocumentUpload';
import useTranslate from '~/hooks/useTranslate';
import { IFile } from '~/interfaces/shared';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { usePostClientAssessmentAnswersMutation } from '~/store/apis/endpoints/clientAssessmentApi/clientAssessment.api';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardStepContent,
  StandardStepContentBody,
  StandardStepContentFooter,
} from '~/styles/BaseStyledComponents';
import { LastBasicStepSummaryBox } from '../Steps/LastBasicStepSummaryBox';
import useGoToNextStep from '../Steps/useGoToNextStep';
import {
  createLegalQuestionsFormSchema,
  LegalQuestionsForm,
} from './LegalQuestions.schema';
import {
  BelgiumClientAssessment,
  belgiumClientAssessmentAnswerTranslationById,
  belgiumClientAssessmentQuestionTranslationById,
  mapFormDataToPostClientAssessmentAnswersBody,
  otherDebtAmountNameById,
} from './LegalQuestions.utils';

export const LegalQuestions = () => {
  const clientId = useSelector(
    (state: RootState) => state.loanApplication.clientId
  );

  if (!clientId) {
    throw new Error(
      'Client ID is missing when rendering legal questions step.'
    );
  }

  const { market, language } = useSelector((state: RootState) => state.intl);
  const applicationApiStatus = useSelector(
    (state: RootState) => state.loanApplication.apiStatus
  );

  const [
    postClientAssessmentAnswers,
    { isLoading: isPostClientAssessmentAnswersLoading },
  ] = usePostClientAssessmentAnswersMutation();

  const schema = createLegalQuestionsFormSchema(market);

  const formMethods = useForm<LegalQuestionsForm>({
    schema,
    mode: 'onTouched',
  });

  const {
    register,
    watch,
    setValue,
    setError,
    getValues,
    resetField,
    formState: { isValid, errors },
  } = formMethods;

  const isMarried = watch('isMarried');
  const hasPrenuptialAgreement = watch('hasPrenuptialAgreement');
  const hasOtherDebt = watch('hasOtherDebt');

  const { documentsApiStatus, uploadDocuments } = useDocumentUpload({
    documentType: DocumentType.Other,
  });
  const dispatch = useAppDispatch();
  const t = useTranslate();
  const onNextStepClick = useGoToNextStep();

  const [displayedAddress, setDisplayedAddress] = useState('');
  const [localFiles, setLocalFiles] = useState<IFile[]>([]);

  const {
    IsMarried,
    HasPrenuptialAgreement,
    HasOtherDebt,
  } = BelgiumClientAssessment;

  const isMarriedOptions = [
    {
      label: t(
        belgiumClientAssessmentAnswerTranslationById[IsMarried.answerYes.id]
      ) as string,
      value: IsMarried.answerYes.id,
    },
    {
      label: t(
        belgiumClientAssessmentAnswerTranslationById[IsMarried.answerNo.id]
      ) as string,
      value: IsMarried.answerNo.id,
    },
  ];

  const hasPrenuptialAgreementOptions = [
    {
      label: t(
        belgiumClientAssessmentAnswerTranslationById[
          HasPrenuptialAgreement.answerYes.id
        ]
      ) as string,
      value: HasPrenuptialAgreement.answerYes.id,
    },
    {
      label: t(
        belgiumClientAssessmentAnswerTranslationById[
          HasPrenuptialAgreement.answerNo.id
        ]
      ) as string,
      value: HasPrenuptialAgreement.answerNo.id,
    },
  ];

  const hasOtherDebtOptions = [
    {
      label: t(
        belgiumClientAssessmentAnswerTranslationById[HasOtherDebt.answerYes.id]
      ) as string,
      value: HasOtherDebt.answerYes.id,
    },
    {
      label: t(
        belgiumClientAssessmentAnswerTranslationById[HasOtherDebt.answerNo.id]
      ) as string,
      value: HasOtherDebt.answerNo.id,
    },
  ];

  const otherDebtAmountOptions = Object.entries(otherDebtAmountNameById).map(
    ([answerId, name]) => ({
      label: name,
      value: answerId,
    })
  );

  const onAddressSearchChange = (value: string) => {
    setDisplayedAddress(value);
    resetField('spouse.address');
  };

  const translateFieldError = (message?: string) => {
    return message && t(message);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();

    setValue(name as FieldPath<LegalQuestionsForm>, trimmedValue as any, {
      shouldValidate: true,
      shouldDirty: true,
    });
    dispatch(
      pushToGtmLoanAction({
        actionName: `${name}_change`,
      })
    );
  };

  const submitLegalQuestionsForm = async () => {
    if (localFiles.length > 0) {
      await uploadDocuments(localFiles);
    }

    const formData = schema.parse(getValues());

    const response = await postClientAssessmentAnswers({
      clientId,
      typeId: ClientAssessmentQuestionnaireTypeId.LegalQuestionsBelgium,
      body: mapFormDataToPostClientAssessmentAnswersBody(formData),
    });

    if ('error' in response) {
      logToSentry(response.error, 'apiPostClientAssessmentAnswers');
      throw new Error('Failed to post client assessment answers.');
    }

    onNextStepClick();
  };

  useEffect(() => {
    const isMarriedWithoutPrenup =
      isMarried === IsMarried.answerYes.id &&
      hasPrenuptialAgreement === HasPrenuptialAgreement.answerNo.id;

    // Reset spouse information fields (errors, dirty) and preserve the user's entered values,
    // when toggling answers for isMarried and hasPrenuptialAgreement.
    if (isMarriedWithoutPrenup) {
      resetField('spouse.firstName', {
        defaultValue: getValues('spouse.firstName'),
      });

      resetField('spouse.lastName', {
        defaultValue: getValues('spouse.lastName'),
      });

      if (!displayedAddress) {
        resetField('spouse.address');
      }
    }

    const shouldClearUploadedFiles =
      isMarried === IsMarried.answerNo.id ||
      hasPrenuptialAgreement === HasPrenuptialAgreement.answerNo.id;

    if (shouldClearUploadedFiles) {
      setLocalFiles([]);
    }
  }, [isMarried, hasPrenuptialAgreement]);

  return (
    <StandardStepContent data-testid="legal-questions-step">
      <StandardStepContentBody>
        <StepHeader
          title={'Onboarding.LegalQuestionsStepTitle'}
          description={'Onboarding.LegalQuestionsStepDescription'}
        />
        <Body size="lg">
          {t(
            belgiumClientAssessmentQuestionTranslationById[IsMarried.questionId]
          )}
        </Body>
        <Controller
          name="isMarried"
          control={formMethods.control}
          render={({ field }) => (
            <RadioGroup
              options={isMarriedOptions}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
              }}
            />
          )}
        />

        {isMarried === IsMarried.answerYes.id && (
          <>
            <Body size="lg">
              {t(
                belgiumClientAssessmentQuestionTranslationById[
                  HasPrenuptialAgreement.questionId
                ]
              )}
            </Body>
            <Controller
              name="hasPrenuptialAgreement"
              control={formMethods.control}
              render={({ field }) => (
                <RadioGroup
                  options={hasPrenuptialAgreementOptions}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                />
              )}
            />

            {hasPrenuptialAgreement === HasPrenuptialAgreement.answerYes.id && (
              <>
                <Body size="lg">
                  {t(
                    'Onboarding.LegalQuestionsStepPrenuptialAgreementUploadText'
                  )}
                </Body>
                <DocumentUploadSection
                  fileConfig={fileConfigByDocumentType[DocumentType.Other]}
                  hasApiError={documentsApiStatus === ApiStatus.Failed}
                  onFilesChange={setLocalFiles}
                />
              </>
            )}

            {hasPrenuptialAgreement === HasPrenuptialAgreement.answerNo.id && (
              <>
                <Body size="lg">
                  {t('Onboarding.LegalQuestionsStepSpouseInformation')}
                </Body>
                <Input.Group orientation="vertical">
                  <Input.Group w="100%">
                    <TextInput
                      {...register('spouse.firstName', { onBlur })}
                      label={t('FirstName')}
                      placeholder={t('FirstName') as string}
                      error={translateFieldError(
                        (errors as any)?.spouse?.firstName?.message
                      )}
                      style={{ width: '100%' }}
                    />
                    <TextInput
                      {...register('spouse.lastName', { onBlur })}
                      label={t('LastName')}
                      placeholder={t('LastName') as string}
                      error={translateFieldError(
                        (errors as any)?.spouse?.lastName?.message
                      )}
                      style={{ width: '100%' }}
                    />
                  </Input.Group>
                  <TextInput
                    {...register('spouse.personalNumber', {
                      setValueAs: (value) =>
                        formatPersonalNumber(value || '', market),
                      onBlur,
                    })}
                    label={t('PersonalNumber')}
                    placeholder={getPersonalNumberPlaceholder(market, language)}
                    error={translateFieldError(
                      (errors as any)?.spouse?.personalNumber?.message
                    )}
                  />
                  <Controller
                    name="spouse.address"
                    control={formMethods.control}
                    render={({ field, fieldState }) => (
                      <AddressSearch
                        label={t('Address')}
                        initialValue={displayedAddress}
                        handleResult={(selectedAddress) => {
                          if (selectedAddress) {
                            const {
                              country,
                              street,
                              building,
                              locality,
                              postcode,
                            } = selectedAddress.address;

                            const address = `${locality}, ${street} ${building}, ${postcode}, ${country}`;

                            field.onChange(address, { shouldValidate: true });
                          }
                        }}
                        onChange={onAddressSearchChange}
                        onBlur={() => {
                          if (field.value === undefined && displayedAddress) {
                            setError('spouse.address', {
                              type: 'manual',
                              message:
                                'ValidationErrors.NotSelectedFromAddressSearchResults',
                            });
                          } else {
                            field.onBlur();
                          }
                        }}
                        error={translateFieldError(fieldState.error?.message)}
                      />
                    )}
                  />
                </Input.Group>
              </>
            )}
          </>
        )}

        <Body size="lg">{t('Onboarding.LegalQuestionsStepOtherDebt')}</Body>
        <Controller
          name="hasOtherDebt"
          control={formMethods.control}
          render={({ field }) => (
            <RadioGroup
              options={hasOtherDebtOptions}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
              }}
            />
          )}
        />

        {hasOtherDebt === HasOtherDebt.answerYes.id && (
          <Controller
            name="otherDebtAmount"
            control={formMethods.control}
            render={({ field }) => (
              <Select
                label={t('Onboarding.LegalQuestionsStepDebtAmount')}
                data={otherDebtAmountOptions}
                value={field.value ? String(field.value) : undefined}
                onChange={(value) => field.onChange(Number(value))}
                allowDeselect={false}
              />
            )}
          />
        )}

        {isValid && <LastBasicStepSummaryBox />}
      </StandardStepContentBody>

      <StandardStepContentFooter>
        <NextButton
          label="Onboarding.Submit"
          isLoading={
            applicationApiStatus.patch === ApiStatus.Started ||
            documentsApiStatus === ApiStatus.Started ||
            isPostClientAssessmentAnswersLoading
          }
          action={submitLegalQuestionsForm}
          disabled={!isValid}
        />

        <Stack spacing={'sm'}>
          {marketHasTrustpilot[market] && (
            <TrustpilotWidget templateId="5419b637fa0340045cd0c936" />
          )}
          <Body size="sm" display="block">
            {t('Onboarding.ApplicationProcessingConsent')}
          </Body>
        </Stack>
      </StandardStepContentFooter>
    </StandardStepContent>
  );
};
